import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface Subscription {
  id: string;
  tenant_id: string;
  plan_name: string;
  base_monthly_fee: number;
  per_user_monthly_fee: number;
  licensed_user_count: number;
  is_active: boolean;
  billing_cycle_start_date: string;
  next_billing_date: string;
  subscription_status: 'active' | 'suspended' | 'cancelled' | 'trial';
  trial_end_date: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string | null;
}

export interface SubscriptionCost {
  base_fee: number;
  per_user_fee: number;
  licensed_user_count: number;
  actual_user_count: number;
  total_monthly_cost: number;
}

interface CreateSubscriptionData {
  tenant_id: string;
  plan_name: string;
  base_monthly_fee: number;
  per_user_monthly_fee: number;
  licensed_user_count: number;
  billing_cycle_start_date: string;
  next_billing_date: string;
  subscription_status: 'active' | 'suspended' | 'cancelled' | 'trial';
  trial_end_date?: string | null;
  notes?: string | null;
}

interface UpdateSubscriptionData extends Partial<CreateSubscriptionData> {
  id: string;
}

export function useSubscriptions() {
  const queryClient = useQueryClient();

  // Fetch all subscriptions
  const { data: subscriptions, isLoading } = useQuery({
    queryKey: ['subscriptions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenant_subscriptions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Subscription[];
    },
  });

  // Fetch single subscription by tenant ID
  const fetchSubscription = (tenantId: string) => {
    return useQuery({
      queryKey: ['subscription', tenantId],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('tenant_subscriptions')
          .select('*')
          .eq('tenant_id', tenantId)
          .single();

        if (error && error.code !== 'PGRST116') throw error;
        return data as Subscription | null;
      },
      enabled: !!tenantId,
    });
  };

  // Fetch subscription cost calculation
  const fetchSubscriptionCost = (tenantId: string) => {
    return useQuery({
      queryKey: ['subscription-cost', tenantId],
      queryFn: async () => {
        const { data, error } = await supabase
          .rpc('calculate_subscription_cost', { p_tenant_id: tenantId });

        if (error) throw error;
        return data?.[0] as SubscriptionCost | null;
      },
      enabled: !!tenantId,
    });
  };

  // Create subscription
  const createSubscription = useMutation({
    mutationFn: async (data: CreateSubscriptionData) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: subscription, error } = await supabase
        .from('tenant_subscriptions')
        .insert([{ ...data, created_by: user.id }])
        .select()
        .single();

      if (error) throw error;
      return subscription;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['subscription', variables.tenant_id] });
      queryClient.invalidateQueries({ queryKey: ['subscription-cost', variables.tenant_id] });
      toast.success('Subscription created successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create subscription');
    },
  });

  // Update subscription
  const updateSubscription = useMutation({
    mutationFn: async ({ id, ...data }: UpdateSubscriptionData) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: subscription, error } = await supabase
        .from('tenant_subscriptions')
        .update({ ...data, updated_by: user.id })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return subscription;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['subscription', data.tenant_id] });
      queryClient.invalidateQueries({ queryKey: ['subscription-cost', data.tenant_id] });
      toast.success('Subscription updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update subscription');
    },
  });

  // Toggle subscription status
  const toggleSubscriptionStatus = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('tenant_subscriptions')
        .update({ is_active, updated_by: user.id })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['subscription', data.tenant_id] });
      toast.success(data.is_active ? 'Subscription activated' : 'Subscription deactivated');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update subscription status');
    },
  });

  // Cancel subscription
  const cancelSubscription = useMutation({
    mutationFn: async (id: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('tenant_subscriptions')
        .update({ 
          subscription_status: 'cancelled',
          is_active: false,
          updated_by: user.id 
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['subscription', data.tenant_id] });
      toast.success('Subscription cancelled');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to cancel subscription');
    },
  });

  // Calculate summary statistics
  const summaryStats = {
    totalMRR: subscriptions?.reduce((sum, sub) => {
      if (sub.is_active && sub.subscription_status === 'active') {
        const cost = Number(sub.base_monthly_fee) + (Number(sub.per_user_monthly_fee) * sub.licensed_user_count);
        return sum + cost;
      }
      return sum;
    }, 0) || 0,
    totalARR: (subscriptions?.reduce((sum, sub) => {
      if (sub.is_active && sub.subscription_status === 'active') {
        const cost = Number(sub.base_monthly_fee) + (Number(sub.per_user_monthly_fee) * sub.licensed_user_count);
        return sum + cost;
      }
      return sum;
    }, 0) || 0) * 12,
    activeCount: subscriptions?.filter(s => s.is_active && s.subscription_status === 'active').length || 0,
  };

  return {
    subscriptions,
    isLoading,
    summaryStats,
    fetchSubscription,
    fetchSubscriptionCost,
    createSubscription,
    updateSubscription,
    toggleSubscriptionStatus,
    cancelSubscription,
  };
}
